import { Component, OnInit, OnDestroy } from '@angular/core';
import { CanvasService } from '../../../services/canvas/canvas.service';
import { ShapeService } from '../../shape/shape.service';
import { Observable, of } from 'rxjs';
import {
  ContainerMenuItem,
  CellAlignment,
} from '../../../elements/resource/types/shape.type';
import {
  containerMenu,
  ContainerRectangle,
  selectAbsoluteCellAlignments,
  selectContainerRectangles,
} from '../../store/selector/editor.selector';
import { catchError, map, mapTo } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ContainerShape } from '../../shape/shapes/container/container.shape';
import { IRIToId } from '../../../util/iri';
import { PlaceholderShape } from '../../shape/shapes/placeholder/placeholder-shape';
import { RootShape } from '../../shape/shapes/general/root/root-shape';
import {
  selectShape,
  setContainerItemAction,
} from '../../store/editor.actions';

@Component({
  selector: 'nw-container-panel',
  templateUrl: './container-panel.component.html',
  styleUrls: ['./container-panel.component.scss'],
})
export class ContainerPanelComponent implements OnInit, OnDestroy {
  baseMode = false;
  containerMenus$: Observable<ContainerMenuItem[]>;
  absoluteCellAlignments$: Observable<Record<string, CellAlignment>>;
  dragHoverStates: Record<string, boolean> = {};
  isFileDragActive = false;
  imageUploadState = 'initial';
  private dragCounter = 0;
  orientationLines$: Observable<any[]>;

  isMenuHovered = '';
  containerMouseOver(event: Event, { IRI, state }: ContainerMenuItem) {
    this.store.dispatch(setContainerItemAction({ IRI, action: 'mouseenter' }));
  }

  containerMouseOut(event: Event, { IRI, state }: ContainerMenuItem) {
    this.store.dispatch(setContainerItemAction({ IRI, action: 'mouseleave' }));
    this.isMenuHovered = '';
  }

  containerMenuClick(event: Event, { IRI, state }: ContainerMenuItem) {
    if (state != 'active') {
      event.stopPropagation();
    }
    console.log('containerMenuClick', IRI);
    const shape = this.shapeService.getShapeByIRI(IRI);
    this.store.dispatch(setContainerItemAction({ IRI, action: 'click' }));
    shape?.select();
  }

  closeIconPosition({ x, y, w, h, topology }: ContainerMenuItem) {
    return {
      left: x + w - 16 + 'px',
      top: y + 1 + 'px',
    };
  }
  topCenterPosition({ x, y, w, h, topology }: ContainerMenuItem) {
    return {
      left: x + w / 2 - 6 + 'px',
      top: y - 6 + 'px',
    };
  }

  bottomCenterPosition({ x, y, w, h, topology }: ContainerMenuItem) {
    return {
      left: x + w / 2 - 6 + 'px',
      top: y + h - 6 + 'px',
    };
  }

  leftCenterPosition({ x, y, w, h, topology }: ContainerMenuItem) {
    return {
      left: x - 6 + 'px',
      top: y + h / 2 - 6 + 'px',
    };
  }

  rightCenterPosition({ x, y, w, h, topology }: ContainerMenuItem) {
    return {
      left: x + w - 6 + 'px',
      top: y + h / 2 - 6 + 'px',
    };
  }

  containerRectangles$: Observable<ContainerRectangle[]>;

  constructor(
    private cs: CanvasService,
    private readonly shapeService: ShapeService,
    private readonly store: Store,
  ) {
    // Initialize absolute cell alignments selector (separate from container menu)
    this.absoluteCellAlignments$ = this.store.select(
      selectAbsoluteCellAlignments,
    );

    this.containerRectangles$ = this.store
      .select(selectContainerRectangles)
      .pipe(
        map(rectangles =>
          rectangles
            .filter(rect => rect.alignment)
            .map(rect => {
              const { x, y, w, h } = rect.alignment;
              const { x: xa, y: ya } = this.cs._getAbsoluteCoords(x, y);
              return {
                ...rect,
                alignment: {
                  ...rect.alignment,
                  x: xa,
                  y: ya,
                  w: w * this.cs.canvasScale,
                  h: h * this.cs.canvasScale,
                },
              };
            }),
        ),
      );
    // -- // -- //

    this.absoluteCellAlignments$.subscribe(val => {
      /* console.log(
        'absoluteCellAlignments',
        Object.entries(val || {}).filter(
          ([IRI]) =>
            this.shapeService.getShapeByIRI(IRI)?.getType() ===
            'placeholder-shape',
        ),
      ); */
    });

    // Keep container menu separate - only for action buttons
    this.containerMenus$ = this.cs.store.select(containerMenu).pipe(
      map(
        menu => [],

        // Object.values(menu).map((menu: ContainerMenuItem) => {
        //   const { x, y, w, h } = menu;
        //   const { x: xa, y: ya } = this.cs._getAbsoluteCoords(x, y);
        //   return {
        //     ...menu,
        //     x: xa,
        //     y: ya,
        //     w: w * this.cs.canvasScale,
        //     h: h * this.cs.canvasScale,
        //   } as ContainerMenuItem;
        // }),
      ),
      catchError(e => {
        console.error('catch-error > containerMenus$', e);
        return of(null);
      }),
    );

    this.store.select(selectContainerRectangles).subscribe(val => {
      // console.log('selectContainerRectangles', val);
    });

    // Calculate orientation lines from container menu items
    this.orientationLines$ = this.containerMenus$.pipe(
      map(menuItems => {
        const lines: any[] = [];

        menuItems.forEach(item => {
          const shape = this.shapeService.getShapeByIRI(item.IRI);
          // const container = shape?.parent as ContainerShape;
          const topology =
            shape?.descriptor?.topology ||
            shape?.cellAlignment?.topology ||
            'row';

          // console.log('topology', topology);

          if (!(shape instanceof ContainerShape)) {
            return;
          }
          if (shape.shapes.length == 0) {
            return;
          }

          let gap: number;

          // -- // -- //
          let { w: width, h: height } = shape.cellAlignment;

          width = width * this.cs.canvasScale;
          height = height * this.cs.canvasScale;

          /* 
          console.log(
            'cellAlignments',
            shape.shapes.map(shape => shape.cellAlignment),
          ); 
          */
          const [xa, ya] = shape.getAbsCoords();

          for (let i = 1; i < shape.shapes.length; i++) {
            // -- // -- //
            const childShape = shape.shapes[i];
            const { x: xr, y: yr, w, h } = childShape.cellAlignment;

            const { x, y } = this.cs._getAbsoluteCoords(xa + xr, ya + yr);
            if (topology === 'row') {
              // Vertical orientation lines for column layout //
              lines.push({
                x,
                y: y + height * 0.1,
                width: 2,
                height: height * 0.8,
                type: 'vertical',
              });
            } else {
              lines.push({
                x: x + width * 0.1,
                y,
                width: width * 0.8,
                height: 2,
                type: 'horizontal',
              });
            }

            // console.log('topology', topology);
            // Horizontal orientation lines for row layout
          }
        });

        // console.log('lines', lines);
        return lines;
      }),
    );
  }

  ngOnInit() {
    // Listen for global drag events to detect when files are being dragged
    document.addEventListener('dragenter', this.onGlobalDragEnter.bind(this));
    document.addEventListener('dragleave', this.onGlobalDragLeave.bind(this));
    document.addEventListener('drop', this.onGlobalDrop.bind(this));
  }

  click(IRI: string) {
    this.store.dispatch(selectShape({ IRI }));
    // -- // -- // -- // -- //
  }

  ngOnDestroy() {
    // Clean up global event listeners
    document.removeEventListener(
      'dragenter',
      this.onGlobalDragEnter.bind(this),
    );
    document.removeEventListener(
      'dragleave',
      this.onGlobalDragLeave.bind(this),
    );
    document.removeEventListener('drop', this.onGlobalDrop.bind(this));
  }
  get top() {
    return this.cs.canvasHeight / 2 - this.componentHeight / 2 + 'px';
  }

  get left() {
    return this.cs.canvasWidth / 2 - this.componentWidth / 2 + 'px';
  }
  get componentWidth() {
    return 200;
  }
  get componentHeight() {
    return 100;
  }

  mouseOver(IRI: string) {
    this.cs.controlPanelMouseOver = IRI;
  }
  mouseOut() {
    this.cs.controlPanelMouseOver = null;
  }

  addTextShape(IRI: string) {
    if (this.cs.previewShape.IRI == IRI) {
      this.cs.previewShape._addTextShape();
      return;
    }
    // this.removeContainerMenuItem(IRI); // -- //
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container._addTextShape(shape);
  }

  addImageShape(IRI: string) {
    // Trigger file input for image selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        const fileList = {
          files: target.files,
          coords: [0, 0] as [number, number],
        };
        await this.onFileDropped(fileList, IRI);
      }
    };
    input.click();
  }

  addAbove(IRI: string) {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.addAbove(shape);
  }

  addBelow(IRI: string) {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.addBelow(shape);
  }
  addNext(IRI: string) {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.addItemAfter(shape);
    // container.addShape(this.shapeService.addPlaceholderShape(1)); //
  }
  addPrev(IRI: string) {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.addItemBefore(shape);
  }
  removeContainerMenuItem(IRI: string) {
    // console.log('remove-container-menu', IRI); // -- // -- //
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.removeItem(shape);
  }
  addRow(IRI: string) {
    // -- //
    const shape = this.shapeService.getShapeByIRI(IRI);
    if (shape.getType() == 'root-shape') {
      return (shape as RootShape).addRowShape();
    }
    const container = shape.parent as ContainerShape;
    container.addRowShape(shape);
  }

  addColumn(IRI: string) {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    container.addColumnShape(shape);
  }

  // Helper method to get container topology for positioning buttons
  getContainerTopology(IRI: string): 'row' | 'column' | 'grid' | 'float' {
    const shape = this.shapeService.getShapeByIRI(IRI);
    const container = shape.parent as ContainerShape;
    return container?.descriptor?.topology || 'row';
  }

  // Global drag event handlers
  onGlobalDragEnter(event: DragEvent) {
    // Check if files are being dragged
    if (event.dataTransfer?.types.includes('Files')) {
      this.dragCounter++;
      this.isFileDragActive = true;
    }
  }

  onGlobalDragLeave(event: DragEvent) {
    if (event.dataTransfer?.types.includes('Files')) {
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isFileDragActive = false;
      }
    }
  }

  onGlobalDrop(event: DragEvent) {
    this.dragCounter = 0;
    this.isFileDragActive = false;
  }

  // Local drag event handlers for specific containers
  onDragOver(event: DragEvent, containerIRI: string) {
    console.log('onDragOver');
    event.preventDefault();
    event.stopPropagation();
    this.dragHoverStates[containerIRI] = true;
  }

  onDragLeave(event: DragEvent, containerIRI: string) {
    event.preventDefault();
    event.stopPropagation();
    this.dragHoverStates[containerIRI] = false;
  }

  async onDrop(event: DragEvent, containerIRI: string) {
    event.preventDefault();
    event.stopPropagation();
    this.dragHoverStates[containerIRI] = false;

    const files = event.dataTransfer?.files;
    console.log('onDrop', files);
    if (files && files.length > 0) {
      const coords: [number, number] = [event.offsetX, event.offsetY];
      await this.onFileDropped({ files, coords }, containerIRI);
    }
  }

  async onFileDropped(
    event: { files: FileList; coords: [number, number] },
    containerIRI: string,
  ) {
    this.imageUploadState = 'in-progress';
    const { files, coords } = event;
    const file = files[0];

    console.log('Container file dropped:', { files, coords, containerIRI });

    const extension = file.name.split('.').pop()?.toLowerCase();

    if (file.type.endsWith('svg+xml')) {
      // Handle SVG files if needed
      console.log('SVG file dropped on container');
      return;
    }

    if (this.cs.previewShape) {
      const { data: base64, error: toBase64Error } =
        await this.cs.toBase64(file);

      if (toBase64Error) {
        console.error('Base64 conversion error:', toBase64Error.message);
        this.imageUploadState = 'error';
        return;
      }

      // Get the placeholder shape that was dropped on
      const targetShape = this.shapeService.getShapeByIRI(containerIRI);

      console.log('target', targetShape);
      console.log('target', targetShape.index);
      // Only proceed if the target is a placeholder shape
      if (targetShape.getType() !== 'placeholder-shape') {
        console.log('go-for-it', targetShape.index);
        console.log('File dropped on non-placeholder shape, ignoring');
        this.imageUploadState = 'complete';
        return;
      }

      const IRI = this.shapeService.getNewIRI();
      (targetShape.parent as ContainerShape).replacePlaceholderWithImage(
        targetShape as PlaceholderShape,
        base64,
        IRI,
      );

      // Save image to S3
      const { error } = await this.cs.httpService.saveImageToS3(
        base64,
        IRIToId(IRI),
        extension,
      );

      if (error) {
        console.error('Image could not be saved to S3:', error.message);
        this.imageUploadState = 'error';
        return;
      }

      // Replace the placeholder with the image shape in the container

      // Re-align and update container

      this.imageUploadState = 'complete';
      console.log('Placeholder successfully replaced with image');
    }
  }

  onDragHover(isHovering: boolean, containerIRI: string) {
    this.dragHoverStates[containerIRI] = isHovering;
  }

  // Helper methods for template
  isPlaceholderShape(shapeIRI: string): boolean {
    const shape = this.shapeService.getShapeByIRI(shapeIRI);
    return (
      shape?.getType() === 'placeholder-shape' ||
      shape?.getType() === 'root-shape'
    );
  }

  getAbsoluteX(cellAlignment: CellAlignment): number {
    const { x } = this.cs._getAbsoluteCoords(
      cellAlignment.x || 0,
      cellAlignment.y || 0,
    );
    return x;
  }

  getAbsoluteY(cellAlignment: CellAlignment): number {
    const { y } = this.cs._getAbsoluteCoords(
      cellAlignment.x || 0,
      cellAlignment.y || 0,
    );
    return y;
  }

  getScaledWidth(cellAlignment: CellAlignment): number {
    return (cellAlignment.w || 0) * this.cs.canvasScale;
  }

  getScaledHeight(cellAlignment: CellAlignment): number {
    return (cellAlignment.h || 0) * this.cs.canvasScale;
  }
}
